import { mapGetters, mapState } from 'vuex';
import axios from 'axios';
import defaultMixin from './defaultMixin';

export default {
    mixins: [defaultMixin],
    methods: {
        toCurrency(value) {
            if (typeof value !== "number") {
                return value;
            }
            var formatter = new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 2
            });
            return formatter.format(value);
        },
        handleRedirect() {
            if (this.redirect) {
                window.top.location.href = this.redirect;
            } else if (this.$store.state.form.form.integration == 'scheduling') {
                this.setConfirmed();
            } else {
                this.$router.push({
                    name: 'thank-you',
                    params: {
                        height: this.height,
                        message: this.inline_message,
                        title: this.inline_title
                    }
                });
            }
        },
        handleServerValidation(errors) {
          var getFieldFromChildren = function (children, id, _callback) {
              if (children.length > 0) {
                  if (!children[0].$attrs.index) {
                      return _callback(children[0].$children, id, _callback);
                  } else {
                      return children.find((field) => field.$attrs.index === id);
                  }
              }
          };
          const affectedFields = Object.keys(errors).map((id) => {
            return {
              field: getFieldFromChildren(this.$children, id, getFieldFromChildren),
              message: errors[id].value[0]
            };
          });
          affectedFields.forEach((field) => {
            field.field.serverError = true;
            field.field.serverMessage = field.message;
          });
        },
        validateForm(children) {
          children.forEach(child => child.hasOwnProperty('setValue') && child.setValue(child.value));
        },
        validateOnTouch(children) {
          children.forEach(child => child.hasOwnProperty('_isVue') && child._isVue === true && child.$v && child.$v.value && child.$v.value.$touch())
        },
        hasError(children) {
          let error = false;

          for (const element of children) {
            if (element.error === true) {
              error = true;
              break;
            }
          }

          return error;
        },
        addRequiredFields(body) {
            this.form.fields.forEach((field) => {
                if (field.validator && field.validator.required && !body[field.id]) {
                    body[field.id] = {
                        fieldId: field.id,
                        fieldName: field.name,
                        value: ''
                    };
                }
            });
            return body;
        },
        async getCaptchaResponse() {
          if (this.expectCaptcha !== true) {
            return '';
          }

          return await this.$recaptcha("form");
        },

       /**
        * Builds a payload for GTM
        */
        buildGTMPayload() {
            let name = this.getValueByIntegrationFieldId('77');
            name = this.splitName(name);
            // favor individual fields 'first name', and 'last name'
            // but use the full name if available
            const firstName = this.getValueByIntegrationFieldId('78') || name[0] || '';
            const lastName = this.getValueByIntegrationFieldId('79') || name[1] || '';
            const phone = this.getValueByIntegrationFieldId('184');
            const email = this.getValueByIntegrationFieldId('185');

            return {
                event: 'madform_submit',
                submission: this.removeEmptyValuesFromObject({
                    formName: this.form.name || '',
                    firstName: firstName,
                    lastName:lastName,
                    phone: phone,
                    email: email,
                }),
            }
        },

        /**
         * Send a google tag manager event to the parent window
         */
        sendGTMEvent() {
            if (!parent) {
                return;
            }

            const payload = this.buildGTMPayload()

            const minimumSubmissionFields = 2;
            if (Object.keys(payload.submission).length < minimumSubmissionFields) {
                return;
            }

            parent.postMessage(JSON.stringify(payload), '*');
        },

        /**
         * @returns Returns the HTML value for the utm hidden field
         */
        getUtmUrlValue() {
          const selector = 'input[name="utm-campaign-url"]';
          const utmElement = document.querySelector(selector);
          if (utmElement) {
            return utmElement.value;
          }
          return '';
        },

        getUtmCookieValue() {
          const selector = 'input[name="utm-cookie-url"]';
          const utmElement = document.querySelector(selector);
          if (utmElement) {
            return decodeURIComponent(utmElement.value);
          }
          return '';
        },

        /**
         * @returns True if the UTM campaign url contains no useful parameters
         */
        isUtmEmpty(utm) {
          const whiteList = ['utm_source', 'utm_medium', 'utm_channel', 'utm_campaign',
            'utm_id', 'utm_term', 'utm_content', 'gclid', 'fbclid'];

          try {
            const url = new URL(utm);
            if (url.searchParams.size <= 0) {
              return true;
            }

            for (const tag of whiteList) {
              if (url.searchParams.has(tag)) {
                return false;
              }
            }
          } catch (e) {
            return true;
          }

          return true;
        },

        async postForm(event) {
          try {
            let body = this.$store.getters.response;
            // Add required fields
            body = this.addRequiredFields(body);
            body['_captcha'] = await this.getCaptchaResponse();
            body["_tracking_"] = this.$store.getters.cookie;
            body["_csrf"] = this.csrf;

            const utmUrlValue = this.getUtmUrlValue();
            const utmCookieValue = this.getUtmCookieValue();

            if (!this.isUtmEmpty(utmUrlValue)){
              body['_utm'] = utmUrlValue;
            } else if(!this.isUtmEmpty(utmCookieValue)){
              body['_utm'] = utmCookieValue;
            }

            this.setSubmitted(true);

            let endpoint, response;
            endpoint = `${this.domain}/forms/api/v1/response/${this.$store.state.form.formId}`;
            response = await axios.post(endpoint, body, {
              params: {
                _sig : this.sig
              }
            });

            this.setSubmitted(false);
            event.target.reset();
            this.sendGTMEvent();
            this.handleRedirect();

            return response.data;
          } catch (error) {
            if (error.response.data.errors) {
              this.handleServerValidation(error.response.data.errors);
            } else if (error.response.data.message) {
              this.setSubmissionError(error.response.data.message);
            }
            this.setSubmitted(false);

            return error.response.data;
          }
        },
    },
    computed: {
        ...mapState('form', ['csrf']),
        ...mapGetters({
            formErrors: 'fieldsWithErrors',
            sig: 'sig'
        }),
        ...mapGetters('form', [
            'fields',
            'redirect',
            'inline_title',
            'inline_message',
            'csrf',
            'validateCaptcha'
        ]),
        ...mapState('payments', ['purchases', 'total', 'stripe']),
        errorsExist() {
          return this.formErrors.length >= 1;
        },
        disableSubmit() {
            return this.errorsExist || (this.stripe && this.stripe.paymentRequired===true && !this.cardLoaded) || this.submitted === true;
        },
        submitClass() {
          return {
            'btn-nohover madform-submit': true,
            disabled: this.errorsExist,
          };
        },
        expectCaptcha() {
          return this.validateCaptcha || this.stripe.paymentRequired;
        }
    }
};
